import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import SendIcon from '@mui/icons-material/Send'

import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import BottomButtonGroup, { BUTTON_LOADING_ICON } from '../../component/BottomButtonGroup'
import ListEmptyState from '../../component/ListEmptyState'
import EmbeddedTableContainer from '../../component/EmbeddedTableContainer'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import { formatDateTimeText } from '../../utils/dateDisplay'
import {
    applyRecordDraft,
    clearRecordDraft,
    createEditRecordDraftKey,
    createRecordDraftPayload,
    readRecordDraft,
    saveRecordDraft,
} from './recordDraftUtils'
import { getRecordMailStatus } from './recordMailStatus'
import { getRecordBounceReason } from './recordMailReason'

const DEFAULT_RECORD_SEARCH_SELECT = {
    searchType: 0,
    searchStatus: 99,
    productId: 0,
}

const PANEL_SX = {
    height: '100%',
    p: 2,
    borderRadius: '4px',
    borderColor: 'rgba(148, 163, 184, 0.24)',
    backgroundColor: '#fff',
}

function FormSection({ title, description, children, sx = {} }) {
    return (
        <Paper variant="outlined" sx={{ ...PANEL_SX, ...sx }}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            {children}
        </Paper>
    )
}

function RecordImagePreview({ imageSrc }) {
    return (
        <FormSection
            title="인증 이미지"
            description="사용자가 제출한 완주 인증 이미지를 확인합니다."
        >
            <Box
                sx={{
                    minHeight: 320,
                    maxHeight: 520,
                    borderRadius: '4px',
                    border: '1px solid rgba(148, 163, 184, 0.24)',
                    backgroundColor: '#f8fafc',
                    overflow: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {imageSrc ? (
                    <Box
                        component="img"
                        src={imageSrc}
                        alt="완주 인증 이미지"
                        sx={{
                            width: '100%',
                            maxHeight: 520,
                            objectFit: 'contain',
                            display: 'block',
                        }}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        등록된 인증 이미지가 없습니다.
                    </Typography>
                )}
            </Box>
        </FormSection>
    )
}

function MailStatus({ status, deliveryStatus }) {
    const mailStatus = getRecordMailStatus({ status, deliveryStatus })

    return (
        <FormSection
            title="메일 발송 상태"
            description="SES 접수와 수신 메일 서버의 처리 결과를 구분합니다."
        >
            <Box sx={{ p: 1.5, borderRadius: '4px', backgroundColor: mailStatus.backgroundColor }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: mailStatus.color }}>
                    {mailStatus.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {mailStatus.description}
                </Typography>
            </Box>
        </FormSection>
    )
}

// 상세 조회 실패 시에도 폼 기본값을 명확하게 되돌릴 수 있게 공통 초기값을 사용한다.
const createDefaultParams = () => ({})

// 배번 입력은 기존 정책대로 앞 4자리까지만 저장한다.
const truncateBibNumber = (value = '') => String(value || '').slice(0, 4)

export default function Record(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.recordId
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || DEFAULT_RECORD_SEARCH_SELECT
    const curPage = locationState.curPage || 1
    const listSearch = location.search || ''
    const draftKey = createEditRecordDraftKey(id)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [sending, setSending] = useState(false)
    const [hasDetail, setHasDetail] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [params, setParams] = useState(() => createDefaultParams())
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const [image, setImage] = useState('')
    const [mailLogs, setMailLogs] = useState([])
    const initialDraftSnapshotRef = useRef('')
    const formRef = useRef(null)

    const moveToList = useCallback((snackbarState) => {
        // 저장 후에도 운영자가 보던 완주기록증 신청 목록 조건으로 그대로 복귀시킨다.
        clearRecordDraft(draftKey)
        history.push(createListLinkTarget({
            pathname: '/new-records',
            search: listSearch,
            searchText: searchText,
            searchSelect: searchSelect,
            curPage: curPage,
            ...(snackbarState ? {
                openSnackbar: true,
                severitySnackbar: snackbarState.severity,
                messageSnackbar: snackbarState.message,
            } : {})
        }))
    }, [curPage, draftKey, history, listSearch, searchSelect, searchText])

    const resetDetailState = useCallback((errorMessage) => {
        setParams(createDefaultParams())
        setImage('')
        setMailLogs([])
        setHasDetail(false)
        setDraftReady(false)
        setDetailErrorMessage(errorMessage)
    }, [])

    const getData = useCallback(async () => {
        setLoading(true)
        setHasDetail(false)
        setDraftReady(false)
        setDetailErrorMessage('')

        if (!id) {
            const errorMessage = '필수 파라미터가 없어 완주기록증 신청 상세를 불러올 수 없습니다.'
            resetDetailState(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
            setLoading(false)
            return
        }

        try {
            const [detailResult, logResult] = await Promise.all([
                API.post('/api/v1/admin/record/getDetail', { recordId: id }),
                API.post('/api/v1/admin/record/getMailLogs', { recordId: id }),
            ])

            const detailData = detailResult.data
            const logData = logResult.data

            if (detailData.code === 200) {
                const detail = detailData.data || {}

                if (!detail.recordId) {
                    resetDetailState('완주기록증 신청 상세 정보를 찾을 수 없습니다.')
                    return
                }

                const storedDraft = readRecordDraft(draftKey)
                const restoredDraft = applyRecordDraft({
                    baseParams: detail,
                    draft: storedDraft,
                })

                initialDraftSnapshotRef.current = JSON.stringify(createRecordDraftPayload({
                    params: detail,
                }))

                setImage(detail.image === null ? '' : process.env.REACT_APP_AWS_S3_URL+'/'+detail.image)
                setParams(restoredDraft.params)
                setHasDetail(Boolean(detail?.recordId))
                setDetailErrorMessage('')
                setDraftReady(true)
            } else if (detailData.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = detailData.message || '완주기록증 신청 상세를 불러오지 못했습니다.'
                resetDetailState(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
                return
            }

            if (logData.code === 200) {
                setMailLogs(logData.data || [])
            } else if (logData.code === 403) {
                history.push('/login')
                return
            } else {
                setMailLogs([])
            }
        } catch (_error) {
            const errorMessage = '완주기록증 신청 상세를 불러오지 못했습니다.'
            resetDetailState(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [draftKey, history, id, resetDetailState])

    useEffect(() => {
        getData()
    }, [getData])

    useEffect(() => {
        if (!draftReady || !hasDetail || !params?.recordId) {
            return
        }

        // 완주기록증 신청 상세는 목록 query를 건드리지 않고 편집 draft만 sessionStorage에 보존한다.
        const nextDraft = createRecordDraftPayload({
            params,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearRecordDraft(draftKey)
            return
        }

        saveRecordDraft(draftKey, nextDraft)
    }, [draftKey, draftReady, hasDetail, params])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const handleUpdateField = (e) => {
        let nextValue = e.target.value
        if (e.target.name === 'number') {
            nextValue = truncateBibNumber(e.target.value)
        }
        setParams({
            ...params,
            [e.target.name]: nextValue
        })
    }

    const updateData = async () => {
        try {
            setSaving(true)
            const { data } = await API.post('/api/v1/admin/record/save', { params })
            if (data.code === 200) {
                moveToList({ severity: 'success', message: data.message })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('완주기록증 신청을 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const sendData = async () => {
        if (saving || sending || !formRef.current?.reportValidity()) {
            return
        }

        const actionLabel = Number(params.status) === 1 ? '재발송' : '발송'
        if (!window.confirm(`${params.email || ''} 주소로 완주기록증 메일을 ${actionLabel}하시겠습니까?`)) {
            return
        }

        try {
            setSending(true)
            const { data } = await API.post('/api/v1/admin/record/send', { params })
            if (data.code === 200) {
                moveToList({ severity: 'success', message: data.message })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar(data.code === 409 ? 'warning' : 'error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('완주기록증 메일을 SES에 접수하지 못했습니다.')
        } finally {
            setSending(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving || sending) {
            return
        }

        updateData()
    }

    const getLogLabel = (value) => {
        if (value === 'manual') return '단건'
        if (value === 'batch') return '배치'
        if (value === 'event') return 'SES 이벤트'
        if (value === 'sent') return 'SES 접수'
        if (value === 'delivered') return '메일 서버 전달'
        if (value === 'bounced') return '반송'
        if (value === 'complained') return '스팸 신고'
        if (value === 'rejected') return 'SES 거부'
        if (value === 'delayed') return '배송 지연'
        if (value === 'rendering_failed') return '렌더링 실패'
        if (value === 'failed') return '실패'
        if (value === 'skipped') return '스킵'
        if (value === 'missing_template') return '템플릿 없음'
        if (value === 'invalid_number') return '배번 0000'
        if (value === 'missing_email') return '이메일 없음'
        if (value === 'invalid_email') return '이메일 형식 오류'
        if (value === 'suppressed_bounce') return '반송 억제목록'
        if (value === 'suppressed_complaint') return '신고 억제목록'
        if (value === 'suppression_check_failed') return '억제목록 확인 실패'
        if (value === 'recipient_mail_server_accepted') return '수신 메일 서버 수락'
        if (value === 'send_failed') return '전송 실패'
        if (value === 'exception') return '예외'
        return value || '-'
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="완주기록증 신청 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" path="/new-records" search={listSearch} visible={false} curPage={curPage} searchText={searchText} searchSelect={searchSelect} onBackClick={() => clearRecordDraft(draftKey)} backLabel="목록으로"/>
            )}
            sx={{ mt: 2 }}
        />
    )

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : !hasDetail ? (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>완주기록증 신청 수정</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>완주기록증 신청 수정</Title>
                    <Box ref={formRef} component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: (saving || sending) ? 0.6 : 1 }}>
                        <fieldset disabled={saving || sending} style={{ border: 0, margin: 0, padding: 0 }}>
                            <Grid container spacing={2} sx={{ marginTop: 2 }}>
                                <Grid item xs={12} md={4}>
                                    <RecordImagePreview imageSrc={image}/>
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <FormSection
                                            title="신청 정보"
                                            description="완주기록증에 반영할 신청자 정보를 확인하고 수정합니다."
                                        >
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <FormControl fullWidth>
                                                        <TextField
                                                            label="코스"
                                                            id="course"
                                                            name="course"
                                                            value={params.type ? params.course : params.title + ' ' + params.course}
                                                            onChange={handleUpdateField}
                                                            size="small"
                                                            inputProps={{ readOnly: true }}
                                                        />
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <FormControl fullWidth>
                                                        <TextField
                                                            label="이름"
                                                            id="username"
                                                            name="username"
                                                            value={params.username || ''}
                                                            onChange={handleUpdateField}
                                                            size="small"
                                                            required
                                                        />
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <FormControl fullWidth>
                                                        <TextField
                                                            label="이메일"
                                                            id="email"
                                                            name="email"
                                                            value={params.email || ''}
                                                            onChange={handleUpdateField}
                                                            size="small"
                                                            type="email"
                                                            required
                                                        />
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <FormControl fullWidth>
                                                        <TextField
                                                            label="배번"
                                                            id="number"
                                                            name="number"
                                                            value={params.number || ''}
                                                            onChange={handleUpdateField}
                                                            size="small"
                                                            required
                                                            inputProps={{ type: 'number', pattern: "[0-9]*", inputMode: "numeric" }}
                                                            onKeyPress={(e) => {
                                                                if ( e.key === "e" || e.key === "-" ) {
                                                                    e.preventDefault()
                                                                }
                                                            }}
                                                        />
                                                    </FormControl>
                                                </Grid>
                                            </Grid>
                                        </FormSection>
                                        <MailStatus status={params.status} deliveryStatus={params.deliveryStatus}/>
                                    </Box>
                                </Grid>
                            </Grid>
                        </fieldset>
                        <BottomButtonGroup
                            path="/new-records"
                            search={listSearch}
                            visible={true}
                            curPage={curPage}
                            searchText={searchText}
                            searchSelect={searchSelect}
                            submitDisabled={saving || sending}
                            submitLabel={saving ? '저장 중...' : '저장'}
                            actionDescription="저장은 신청 정보만 변경합니다. 메일은 별도 발송 버튼을 눌러야 SES에 접수됩니다."
                            onBackClick={() => clearRecordDraft(draftKey)}
                            backDisabled={saving || sending}
                            extraActions={(
                                <Button
                                    type="button"
                                    variant="contained"
                                    color="success"
                                    disabled={saving || sending}
                                    onClick={sendData}
                                    startIcon={sending ? BUTTON_LOADING_ICON : <SendIcon/>}
                                    aria-busy={sending}
                                >
                                    {sending ? 'SES 접수 중...' : (Number(params.status) === 1 ? '메일 재발송' : '메일 발송')}
                                </Button>
                            )}
                        />
                    </Box>
                    <Box sx={{ mt: 3 }}>
                        <Title>메일 발송 이력</Title>
                        <EmbeddedTableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>이벤트/기록 시간</TableCell>
                                        <TableCell>제공자</TableCell>
                                        <TableCell>유형</TableCell>
                                        <TableCell>결과</TableCell>
                                        <TableCell>사유</TableCell>
                                        <TableCell>이메일</TableCell>
                                        <TableCell>관리자</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {mailLogs.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center">발송 이력이 없습니다.</TableCell>
                                        </TableRow>
                                    ) : (
                                        mailLogs.map((row) => {
                                            const bounceReason = getRecordBounceReason(row)

                                            return (
                                                <TableRow key={row.recordMailLogId}>
                                                    <TableCell>{formatDateTimeText(row.eventTime || row.regTime)}</TableCell>
                                                    <TableCell>{row.provider || '-'}</TableCell>
                                                    <TableCell>{getLogLabel(row.attemptType)}</TableCell>
                                                    <TableCell>{getLogLabel(row.result)}</TableCell>
                                                    <TableCell>
                                                        {bounceReason ? (
                                                            <Box sx={{ minWidth: 240 }}>
                                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                                    {bounceReason.label}
                                                                </Typography>
                                                                {bounceReason.sesReason && (
                                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                                        SES: {bounceReason.sesReason}
                                                                    </Typography>
                                                                )}
                                                                {bounceReason.smtpReason && (
                                                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: 'break-word' }}>
                                                                        SMTP: {bounceReason.smtpReason}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        ) : getLogLabel(row.reason)}
                                                    </TableCell>
                                                    <TableCell>{row.email || '-'}</TableCell>
                                                    <TableCell>{row.adminId || '-'}</TableCell>
                                                </TableRow>
                                            )
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </EmbeddedTableContainer>
                    </Box>
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
